
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { X } from 'lucide-react';

interface OrderCancelButtonProps {
  orderId: string;
  onCancel: () => void;
  orderStatus: string;
}

const OrderCancelButton = ({ orderId, onCancel, orderStatus }: OrderCancelButtonProps) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // Only show cancel button for pending orders
  if (orderStatus !== 'pending') {
    return null;
  }

  const handleCancelClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmCancel = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.rpc('update_order_status_simple', {
        order_id: orderId,
        new_status: 'cancelled'
      });

      if (error) {
        console.error('Error cancelling order:', error);
        return;
      }

      onCancel();
    } catch (error) {
      console.error('Error cancelling order:', error);
    } finally {
      setLoading(false);
      setShowConfirmDialog(false);
    }
  };

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        onClick={handleCancelClick}
        disabled={loading}
      >
        <X className="h-3 w-3 mr-1" />
        {loading ? 'Cancelling...' : 'Cancel Order'}
      </Button>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Order?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this order? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, Keep Order</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmCancel}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? 'Cancelling...' : 'Yes, Cancel Order'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OrderCancelButton;
